import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/stores/authStore';
import { Landing, Login, Register, Dashboard, VoiceAssistant, Sessions, Settings, AdminDashboard, UserManagement } from '@/pages';
import { Skeleton } from '@/components/ui';

/**
 * Protected Route Component
 * 
 * Redirects to login if user is not authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-950">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-ocean-cyan to-ocean-blue flex items-center justify-center animate-pulse">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Public Route Component
 * 
 * Redirects to dashboard if user is already authenticated
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-950">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-ocean-cyan to-ocean-blue flex items-center justify-center animate-pulse">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Page Transition Wrapper
 */
function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  
  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Main App Component
 */
function App() {
  const { checkAuth } = useAuthStore();

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Routes>
      {/* Public Routes */}
        <Route
          path="/"
          element={
            <PageTransition>
              <Landing />
            </PageTransition>
          }
        />
        
        <Route
          path="/login"
          element={
            <PublicRoute>
              <PageTransition>
                <Login />
              </PageTransition>
            </PublicRoute>
          }
        />
        
        <Route
          path="/register"
          element={
            <PublicRoute>
              <PageTransition>
                <Register />
              </PageTransition>
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <PageTransition>
                <Dashboard />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Placeholder Routes - Will be implemented in later Epics */}
        <Route
          path="/assistant"
          element={
            <ProtectedRoute>
              <PageTransition>
                <VoiceAssistant />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        <Route
          path="/sessions"
          element={
            <ProtectedRoute>
              <PageTransition>
                <Sessions />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <PageTransition>
                <Settings />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <PageTransition>
                <AdminDashboard />
              </PageTransition>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <PageTransition>
                <UserManagement />
              </PageTransition>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute>
              <PageTransition>
                <ComingSoon title="Analytics Dashboard" />
              </PageTransition>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute>
              <PageTransition>
                <ComingSoon title="Audit Logs" />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Catch all - 404 */}
        <Route
          path="*"
          element={
            <PageTransition>
              <NotFound />
            </PageTransition>
          }
        />
      </Routes>
  );
}

/**
 * Coming Soon Placeholder
 */
function ComingSoon({ title }: { title: string }) {
  const { user } = useAuthStore();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-950 p-4">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-ocean-purple to-ocean-pink flex items-center justify-center">
          <span className="text-3xl">🚧</span>
        </div>
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        <p className="text-gray-400 mt-2">
          This feature is coming soon in Epic 2. Stay tuned!
        </p>
        <a
          href="/dashboard"
          className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl bg-ocean-cyan/20 text-ocean-cyan hover:bg-ocean-cyan/30 transition-colors"
        >
          ← Back to Dashboard
        </a>
      </div>
    </div>
  );
}

/**
 * 404 Not Found
 */
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-950 p-4">
      <div className="text-center max-w-md">
        <div className="text-8xl font-bold text-gradient mb-4">404</div>
        <h1 className="text-2xl font-bold text-white">Page Not Found</h1>
        <p className="text-gray-400 mt-2">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a
          href="/"
          className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl bg-ocean-cyan/20 text-ocean-cyan hover:bg-ocean-cyan/30 transition-colors"
        >
          ← Go Home
        </a>
      </div>
    </div>
  );
}

export default App;

